import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { draftsApi } from '../../api/surveys/drafts';
import type { SurveyDraft } from '../../types/surveys/publicSurvey.types';

const DRAFT_KEY_PREFIX = 'survey_draft_';
const SAVE_DELAY = 500; // Reduced to 500ms for better UX

export const useSurveyDraft = (eventSlug: string, surveySlug: string) => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesRef = useRef<any>({});
  const savePromiseRef = useRef<Promise<void> | null>(null);

  // Get auth token from localStorage
  const getStoredAuthToken = useCallback(() => {
    if (!surveySlug) return null;
    return localStorage.getItem(`${DRAFT_KEY_PREFIX}${surveySlug}`);
  }, [surveySlug]);

  // Store auth token in localStorage
  const storeAuthToken = useCallback((authToken: string) => {
    if (!surveySlug) return;
    localStorage.setItem(`${DRAFT_KEY_PREFIX}${surveySlug}`, authToken);
  }, [surveySlug]);

  // Clear auth token from localStorage
  const clearAuthToken = useCallback(() => {
    if (!surveySlug) return;
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${surveySlug}`);
  }, [surveySlug]);

  // Create draft mutation
  const createDraftMutation = useMutation({
    mutationFn: () => 
      draftsApi.createDraft(eventSlug, surveySlug),
    onSuccess: (data) => {
      storeAuthToken(data.authToken);
      queryClient.setQueryData(['surveyDraft', eventSlug, surveySlug], data);
    },
  });

  // Update draft mutation
  const updateDraftMutation = useMutation({
    mutationFn: ({ authToken, updates }: { authToken: string; updates: any }) =>
      draftsApi.updateDraft(eventSlug, surveySlug, authToken, updates),
    onSuccess: (data) => {
      setLastSaved(new Date());
      setSaveError(null);
      setHasPendingChanges(false);
      queryClient.setQueryData(['surveyDraft', eventSlug, surveySlug], data);
    },
    onError: (error) => {
      setSaveError('Failed to save progress');
      console.error('Draft save error:', error);
    },
    onSettled: () => {
      setIsSaving(false);
      savePromiseRef.current = null;
    },
  });

  // Get existing draft
  const { data: draft, isLoading: isDraftLoading } = useQuery<SurveyDraft>({
    queryKey: ['surveyDraft', eventSlug, surveySlug],
    queryFn: async () => {
      const authToken = getStoredAuthToken();
      if (!authToken) {
        // Create new draft
        const newDraft = await createDraftMutation.mutateAsync();
        return newDraft;
      }

      try {
        // Try to get existing draft
        return await draftsApi.getDraft(eventSlug, surveySlug, authToken);
      } catch (error) {
        // If draft not found, create new one
        clearAuthToken();
        const newDraft = await createDraftMutation.mutateAsync();
        return newDraft;
      }
    },
    enabled: !!eventSlug && !!surveySlug,
    staleTime: Infinity, // Don't refetch automatically
  });

  // Save changes with debouncing
  const saveChanges = useCallback((updates: any) => {
    if (!draft?.authToken) return;

    // Merge updates with pending updates
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      ...updates,
    };
    
    setHasPendingChanges(true);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      if (Object.keys(pendingUpdatesRef.current).length > 0) {
        setIsSaving(true);
        const savePromise = updateDraftMutation.mutateAsync({
          authToken: draft.authToken,
          updates: pendingUpdatesRef.current,
        });
        savePromiseRef.current = savePromise;
        pendingUpdatesRef.current = {};
      }
    }, SAVE_DELAY);
  }, [draft?.authToken, updateDraftMutation]);

  // Save immediately and wait for all pending saves
  const saveNow = useCallback(async () => {
    if (!draft?.authToken) return;

    // Clear any pending timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Wait for any in-progress save to complete
    if (savePromiseRef.current) {
      await savePromiseRef.current;
    }

    // Save any pending updates
    if (Object.keys(pendingUpdatesRef.current).length > 0) {
      setIsSaving(true);
      const savePromise = updateDraftMutation.mutateAsync({
        authToken: draft.authToken,
        updates: pendingUpdatesRef.current,
      });
      savePromiseRef.current = savePromise;
      await savePromise;
      pendingUpdatesRef.current = {};
    }
  }, [draft?.authToken, updateDraftMutation]);

  // Clear draft (after submission)
  const clearDraft = useCallback(() => {
    clearAuthToken();
    queryClient.removeQueries({ queryKey: ['surveyDraft', eventSlug, surveySlug] });
  }, [eventSlug, surveySlug, clearAuthToken, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    draft,
    isDraftLoading,
    isSaving,
    hasPendingChanges,
    lastSaved,
    saveError,
    saveChanges,
    saveNow,
    clearDraft,
  };
};